// src/hooks/useReservationForm.js
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import emailjs from "emailjs-com";

const EXTRA_CITY_LIST = [
  "Xi'an",
  "Guilin",
  "Cantón",
  "Chengdu",
  "Shanghái",
  "Otro",
];
const INTERPRETATION_TYPES = [
  "En tiempo real",
  "Acompañamiento a fábrica",
  "Redacción de texto",
  "Otro",
];

export function useReservationForm(tour, onClose) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    startDate: "",
    endDate: "",
    showCities: false,
    extraCityInput: "",
    customExtraCity: "",
    selectedExtraCities: [],
    interpretationCityInput: "",
    selectedInterpretationCities: [],
    interpretationTypeInput: "",
    customInterpretationType: "",
    selectedInterpretationTypes: [],
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const nameInputRef = useRef(null);

  const isInterpretation = useMemo(
    () => tour?.type === "Interpretation",
    [tour]
  );

  // **ESTE ES EL useEffect QUE HEMOS ELIMINADO**
  // useEffect(() => {
  //   if (nameInputRef.current) {
  //     nameInputRef.current.focus();
  //   }
  // }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCheckbox = useCallback((e) => {
    setFormData((prev) => ({ ...prev, showCities: e.target.checked }));
  }, []);

  const handleAddExtraCity = useCallback(() => {
    const city =
      formData.extraCityInput === "Otro"
        ? formData.customExtraCity.trim()
        : formData.extraCityInput;
    if (city && !formData.selectedExtraCities.includes(city)) {
      setFormData((prev) => ({
        ...prev,
        selectedExtraCities: [...prev.selectedExtraCities, city],
        extraCityInput: "",
        customExtraCity: "",
      }));
    }
  }, [
    formData.extraCityInput,
    formData.customExtraCity,
    formData.selectedExtraCities,
  ]);

  const handleRemoveExtraCity = useCallback((cityToRemove) => {
    setFormData((prev) => ({
      ...prev,
      selectedExtraCities: prev.selectedExtraCities.filter(
        (city) => city !== cityToRemove
      ),
    }));
  }, []);

  const availableExtraCities = useMemo(() => {
    return EXTRA_CITY_LIST.filter(
      (city) => !formData.selectedExtraCities.includes(city)
    );
  }, [formData.selectedExtraCities]);

  const handleInterpretationCityInputChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, interpretationCityInput: value }));
  }, []);

  const handleAddInterpretationCity = useCallback(() => {
    const city = formData.interpretationCityInput.trim();
    if (city && !formData.selectedInterpretationCities.includes(city)) {
      setFormData((prev) => ({
        ...prev,
        selectedInterpretationCities: [
          ...prev.selectedInterpretationCities,
          city,
        ],
        interpretationCityInput: "",
      }));
    }
  }, [formData.interpretationCityInput, formData.selectedInterpretationCities]);

  const handleInterpretationCityKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddInterpretationCity();
      }
    },
    [handleAddInterpretationCity]
  );

  const handleRemoveInterpretationCity = useCallback((cityToRemove) => {
    setFormData((prev) => ({
      ...prev,
      selectedInterpretationCities: prev.selectedInterpretationCities.filter(
        (city) => city !== cityToRemove
      ),
    }));
  }, []);

  const handleAddInterpretationType = useCallback(() => {
    const type =
      formData.interpretationTypeInput === "Otro"
        ? formData.customInterpretationType.trim()
        : formData.interpretationTypeInput;
    if (type && !formData.selectedInterpretationTypes.includes(type)) {
      setFormData((prev) => ({
        ...prev,
        selectedInterpretationTypes: [
          ...prev.selectedInterpretationTypes,
          type,
        ],
        interpretationTypeInput: "",
        customInterpretationType: "",
      }));
    }
  }, [
    formData.interpretationTypeInput,
    formData.customInterpretationType,
    formData.selectedInterpretationTypes,
  ]);

  const handleRemoveInterpretationType = useCallback((typeToRemove) => {
    setFormData((prev) => ({
      ...prev,
      selectedInterpretationTypes: prev.selectedInterpretationTypes.filter(
        (type) => type !== typeToRemove
      ),
    }));
  }, []);

  const isFormValid = useCallback(() => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.startDate !== "" &&
      formData.endDate !== ""
    );
  }, [formData]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid()) return;
      setIsSending(true);

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        start_date: formData.startDate,
        end_date: formData.endDate,
        service_type: isInterpretation ? "Interpretación" : "Tour",
        extra_cities: formData.selectedExtraCities.join(", "),
        interpretation_cities: formData.selectedInterpretationCities.join(", "),
        interpretation_types: formData.selectedInterpretationTypes.join(", "),
      };

      emailjs
        .send(
          "service_j14o2l5",
          "template_ytsolu5",
          templateParams,
          "Mo6IyGy5VQoso4O9T"
        )
        .then(
          (response) => {
            console.log("Correo enviado", response);
            setIsSending(false);
            setIsSubmitted(true);
            setTimeout(() => onClose(), 2500);
          },
          (error) => {
            console.error("Error al enviar correo", error);
            setIsSending(false);
            alert(
              "Hubo un problema al enviar tu reserva. Inténtalo más tarde."
            );
          }
        );
    },
    [formData, isFormValid, isInterpretation, onClose]
  );

  return {
    formData,
    isInterpretation,
    isSubmitted,
    isSending,
    availableExtraCities,
    INTERPRETATION_TYPES,
    handleChange,
    handleCheckbox,
    handleAddExtraCity,
    handleRemoveExtraCity,
    handleInterpretationCityInputChange,
    handleInterpretationCityKeyDown,
    handleRemoveInterpretationCity,
    handleAddInterpretationType,
    handleRemoveInterpretationType,
    handleSubmit,
    nameInputRef,
  };
}
